import { Input } from '@@/form-components/Input';
import { Switch } from '@@/form-components/SwitchField/Switch';

import { AlertRuleTier, MetricType } from '../../types';
import { AlertSeverityBadge } from '../../AlertSeverityBadge';
import { ByteThresholdControl } from '../../ByteThresholdControl';
import { canonicalizeAlertRuleTiers } from '../../utils';

import { computeTierErrors } from './tierValidation';

interface TierRowProps {
  tier: AlertRuleTier;
  metricType: MetricType;
  hintId: string;
  error?: string;
  onChange: (updated: AlertRuleTier) => void;
}

function TierRow({ tier, metricType, hintId, error, onChange }: TierRowProps) {
  const errorId = error ? `tier-threshold-error-${tier.severity}` : undefined;
  const describedBy = errorId ? `${hintId} ${errorId}` : hintId;

  return (
    <tr>
      <td className="w-28 py-3 pl-4 pr-2 align-middle">
        <Switch
          id={`tier-enabled-${tier.severity}`}
          name={`tier-enabled-${tier.severity}`}
          checked={tier.enabled}
          onChange={(checked) => onChange({ ...tier, enabled: checked })}
          data-cy={`tier-enabled-${tier.severity}`}
          showStateIcon
        />
      </td>
      <td className="w-40 px-2 py-3 align-middle">
        <AlertSeverityBadge severity={tier.severity} />
      </td>
      <td className="py-3 pl-2 pr-4 align-middle">
        <div className="flex items-center gap-3">
          {metricType === 'bytes' ? (
            <ByteThresholdControl
              value={tier.threshold}
              onChange={(threshold) => onChange({ ...tier, threshold })}
              id={`tier-threshold-${tier.severity}`}
              name={`tier-threshold-${tier.severity}`}
              disabled={!tier.enabled}
              ariaDescribedBy={describedBy}
              className="flex gap-3"
              inputClassName="w-36"
              unitClassName="w-20"
            />
          ) : (
            <div className="w-48">
              <Input
                id={`tier-threshold-${tier.severity}`}
                name={`tier-threshold-${tier.severity}`}
                type="number"
                value={tier.threshold}
                onChange={(event) =>
                  onChange({
                    ...tier,
                    threshold: event.target.valueAsNumber || 0,
                  })
                }
                disabled={!tier.enabled}
                aria-describedby={describedBy}
                data-cy={`tier-threshold-${tier.severity}`}
                min={0}
                max={metricType === 'percentage' ? 100 : undefined}
                step={metricType === 'raw' ? 'any' : undefined}
              />
            </div>
          )}
          {metricType === 'percentage' && (
            <span className="text-muted text-lg leading-none">%</span>
          )}
          {metricType === 'days' && (
            <span className="text-muted text-sm leading-none">days</span>
          )}
        </div>
        {error && (
          <p id={errorId} className="text-danger mt-1 text-xs" role="alert">
            {error}
          </p>
        )}
      </td>
    </tr>
  );
}

interface TierConfigPanelProps {
  tiers: AlertRuleTier[];
  operator: string;
  metricType: MetricType;
  onChange: (tiers: AlertRuleTier[]) => void;
}

export function TierConfigPanel({
  tiers,
  operator,
  metricType,
  onChange,
}: TierConfigPanelProps) {
  const hintId = 'tier-threshold-ordering-hint';
  const errors = computeTierErrors(tiers, operator);
  const ordered = canonicalizeAlertRuleTiers(tiers);

  function handleTierChange(updated: AlertRuleTier) {
    const next = ordered.map((t) =>
      t.severity === updated.severity ? updated : t
    );

    onChange(next);
  }

  return (
    <div data-cy="tier-config-panel">
      <table className="mb-0 table w-full table-fixed">
        <thead>
          <tr>
            <th className="w-28">Active</th>
            <th className="w-40">Severity</th>
            <th>Threshold</th>
          </tr>
        </thead>
        <tbody>
          {ordered.map((tier) => (
            <TierRow
              key={tier.severity}
              tier={tier}
              metricType={metricType}
              hintId={hintId}
              error={errors[tier.severity]}
              onChange={handleTierChange}
            />
          ))}
        </tbody>
      </table>
      <p id={hintId} className="text-muted mt-3 text-sm">
        {operator === '>'
          ? 'Thresholds must increase from info → warning → critical.'
          : 'Thresholds must decrease from info → warning → critical.'}
      </p>
    </div>
  );
}
